import {
  faArrowsRotate,
  faCopy,
  faLightbulb,
  faListUl,
  faPlus,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Flex } from '@mantine/core';
import type { JSX } from 'react';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { EGoogleAdsKeywordMatchType } from '@feathr/blackbox';
import { Button, Label, toast, Well } from '@feathr/components';

import { createKeyword } from '../AdStep.factories';
import type { IAdGroupKeyword } from '../AdStep.types';
import BulkAddKeywordsModal from './BulkAddKeywordsModal';
import { useKeywordIdeas } from './KeywordFields.hooks';
import { getAvailableIdeas, getMatchTypeOptions, getSeedKeywords } from './KeywordFields.utils';
import KeywordRow from './KeywordRow';
import KeywordSuggestions from './KeywordSuggestions';

import * as styles from './AdGroupEditor.css';

interface IKeywordFieldsProps {
  keywords: IAdGroupKeyword[];
  /** Messages for every keyword that has any, keyed by keyword key. */
  keywordErrors: Record<string, string[]>;
  onChange: (keywords: IAdGroupKeyword[]) => void;
  disabled?: boolean;
  /** The saved ad group's id. Undefined before its first save, when nothing can be suggested. */
  adGroupId?: string;
}

/**
 * The positive keywords for one ad group, a row each: the term, the match type it competes
 * on, and a control to drop it. Rows are added blank and edited in place, the way sitelinks
 * are, so a keyword can be corrected rather than removed and retyped.
 *
 * A whole list at once goes through {@link BulkAddKeywordsModal}. The group's keywords can be
 * copied out in the format that modal parses, so a set can move between ad groups — as text
 * only, so a mixed-match-type group comes back at whichever match type the paste chooses.
 *
 * Google's Keyword Ideas can suggest more, once there is an ad group id to ask about.
 *
 * @param props - {@link IKeywordFieldsProps}.
 */
function KeywordFields({
  keywords,
  keywordErrors,
  onChange,
  disabled = false,
  adGroupId,
}: Readonly<IKeywordFieldsProps>): JSX.Element {
  const { t } = useTranslation();
  const [isBulkAddOpen, setIsBulkAddOpen] = useState(false);

  /* One array for every row, so a keystroke does not hand each picker new options. */
  const matchTypeOptions = useMemo(() => getMatchTypeOptions(t), [t]);
  /* Blank rows are lines waiting to be filled, so they are nothing to copy out. */
  const filled = keywords.map((keyword) => keyword.text.trim()).filter(Boolean);
  // Taken from what the editor shows, not what is stored: the wizard holds keyword edits
  // in memory until publish, so seeding from the saved list would answer a stale question.
  const seedKeywords = getSeedKeywords(keywords);
  const {
    ideas,
    isLoading: isLoadingIdeas,
    hasError: hasIdeasError,
    hasFetched,
    canRefresh,
    fetchIdeas,
  } = useKeywordIdeas(adGroupId, seedKeywords);
  // Filtered per render, so removing a keyword offers its suggestion again immediately.
  const availableIdeas = getAvailableIdeas(ideas, keywords);

  function handleAdd(): void {
    onChange([...keywords, createKeyword('')]);
  }

  function handleAddIdea(text: string): void {
    onChange([...keywords, createKeyword(text)]);
  }

  function handleRemove(index: number): void {
    onChange(keywords.filter((_, i) => i !== index));
  }

  function handleTextChange(index: number, text: string): void {
    onChange(keywords.map((keyword, i) => (i === index ? { ...keyword, text } : keyword)));
  }

  function handleMatchTypeChange(index: number, matchType: EGoogleAdsKeywordMatchType): void {
    onChange(keywords.map((keyword, i) => (i === index ? { ...keyword, matchType } : keyword)));
  }

  function handleOpenBulkAdd(): void {
    setIsBulkAddOpen(true);
  }

  function handleCloseBulkAdd(): void {
    setIsBulkAddOpen(false);
  }

  function handleBulkAdd(added: IAdGroupKeyword[]): void {
    onChange([...keywords, ...added]);
  }

  function reportCopyFailure(): void {
    toast(t('Could not copy the keywords to your clipboard. Try again.'), { type: 'error' });
  }

  function handleCopy(): void {
    /* Text only, in the format the bulk add field parses, so a copied set pastes back in. */
    const list = filled.join(', ');
    /* Absent outside a secure context, where calling it would throw before any promise exists. */
    if (!navigator.clipboard?.writeText) {
      reportCopyFailure();
      return;
    }
    navigator.clipboard.writeText(list).then(function onSuccess(): void {
      toast(t('Copied {{count}} keyword.', { count: filled.length }), { type: 'success' });
    }, reportCopyFailure);
  }

  return (
    <>
      <Label weight={'medium'}>{t('Keywords')}</Label>
      <Well layout={'vertical'} theme={'white'} width={'full'}>
        {!!keywords.length && (
          <div className={styles.keywordRows}>
            {/*
             * The columns are named once at the top rather than on every row. Each field
             * still carries its own numbered `aria-label`, which a heading cannot supply.
             */}
            <Label className={styles.columnHeader}>{t('Keyword')}</Label>
            <Label className={styles.columnHeader}>{t('Match type')}</Label>
            {/* Nothing to name above the remove buttons. */}
            <div />
            {keywords.map((keyword, index) => (
              <KeywordRow
                disabled={disabled}
                errors={keywordErrors[keyword.key]}
                key={keyword.key}
                keyword={keyword}
                matchTypeOptions={matchTypeOptions}
                number={index + 1}
                onMatchTypeChange={handleMatchTypeChange}
                onRemove={handleRemove}
                onTextChange={handleTextChange}
              />
            ))}
          </div>
        )}
        {/* A Well's vertical layout stretches its children, so the actions need a row to sit in. */}
        <Flex className={styles.splitActions} wrap={'wrap'}>
          {/* The ones that add to the list travel together, away from the one that reads it out. */}
          <Flex className={styles.actions} wrap={'wrap'}>
            <Button
              disabled={disabled}
              name={'add-keyword'}
              onClick={handleAdd}
              prefix={<FontAwesomeIcon icon={faPlus} />}
            >
              {t('Add keyword')}
            </Button>
            <Button
              disabled={disabled}
              name={'bulk-add-keywords'}
              onClick={handleOpenBulkAdd}
              prefix={<FontAwesomeIcon icon={faListUl} />}
            >
              {t('Bulk add keywords')}
            </Button>
            {/*
              Nothing is suggested until asked for, since every request spends Google quota.
              Once asked, re-asking only helps when the keywords have changed, so the button
              stays disabled until they do. Nothing to ask about before the ad group's first
              save, so the button waits for an id.
            */}
            {!!adGroupId && (
              <Button
                disabled={disabled || isLoadingIdeas || (hasFetched && !canRefresh)}
                /*
                  Google takes around five seconds to answer, so the wait is announced on the
                  control that was clicked rather than only by the skeleton further down.
                */
                isLoading={isLoadingIdeas}
                loadingText={t('Asking Google…')}
                name={'suggest-keywords'}
                onClick={fetchIdeas}
                prefix={<FontAwesomeIcon icon={hasFetched ? faArrowsRotate : faLightbulb} />}
              >
                {hasFetched ? t('Refresh keywords') : t('Suggest keywords')}
              </Button>
            )}
          </Flex>
          <Button
            disabled={disabled || !filled.length}
            name={'copy-keywords'}
            onClick={handleCopy}
            prefix={<FontAwesomeIcon icon={faCopy} />}
          >
            {t('Copy keywords')}
          </Button>
        </Flex>
        <KeywordSuggestions
          disabled={disabled}
          hasError={hasIdeasError}
          hasFetched={hasFetched}
          ideas={availableIdeas}
          isLoading={isLoadingIdeas}
          onAdd={handleAddIdea}
        />
      </Well>
      <BulkAddKeywordsModal
        existingKeywords={keywords}
        onAdd={handleBulkAdd}
        onClose={handleCloseBulkAdd}
        opened={isBulkAddOpen}
      />
    </>
  );
}

export default KeywordFields;
